// ============================================================================
// FINANCE ENGINE — RDA ERP JURÍDICO (Fase 40)
// Artigo 61.1: É TERMINANTEMENTE PROIBIDO o cálculo direto de floats no React.
// Toda operação monetária passa por este utilitário puro.
// ============================================================================

import { TRANSACTION_TYPES, TRANSACTION_STATUS, VIEW_REGIME } from '../config/FinancialSchema';

// ─── SANITIZAÇÃO MATEMÁTICA (Artigo 61.1) ────────────────────────────────────
// Arredondamento bancário: evita erros de ponto flutuante (ex: 0.1 + 0.2 ≠ 0.3)
export function sanitizeAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

// ─── FORMATADOR BRL ───────────────────────────────────────────────────────────
const brlFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatBRL(value) {
  return brlFormatter.format(sanitizeAmount(value));
}

// ─── SOMATÓRIO SANITIZADO ─────────────────────────────────────────────────────
// Soma um array de valores respeitando a sanitização bancária em cada passo
export function sumAmounts(values) {
  return values.reduce((acc, v) => sanitizeAmount(sanitizeAmount(acc) + sanitizeAmount(v)), 0);
}

// ─── FILTRO POR REGIME ────────────────────────────────────────────────────────
// Artigo 59.2: seleciona a data de referência conforme o regime ativo
export function getTransactionDate(tx, regime = VIEW_REGIME.CASH) {
  if (regime === VIEW_REGIME.CASH) {
    return tx?.paymentDate || tx?.competenceDate || '';
  }
  return tx?.competenceDate || '';
}

// ─── FILTRO POR STATUS ATIVO ──────────────────────────────────────────────────
export function isActiveTransaction(tx) {
  return tx?.isDeleted !== true && tx?.status !== TRANSACTION_STATUS.CANCELLED;
}

// ─── CÁLCULO DE SALDO PRINCIPAL ───────────────────────────────────────────────
// Artigo 59.1: aceita initialBalance para permitir saldo inicial configurável
export function calculateBalance(transactions = [], initialBalance = 0, regime = VIEW_REGIME.CASH) {
  const active = transactions.filter(isActiveTransaction);

  let income = 0;
  let expense = 0;

  for (const tx of active) {
    // No regime Caixa, só conta transações liquidadas (PAID)
    if (regime === VIEW_REGIME.CASH && tx.status !== TRANSACTION_STATUS.PAID) continue;

    const amt = sanitizeAmount(tx.amount ?? tx.amountCents ?? 0);

    if (tx.type === TRANSACTION_TYPES.INCOME) {
      income = sanitizeAmount(income + amt);
    } else {
      expense = sanitizeAmount(expense + amt);
    }
  }

  const balance = sanitizeAmount(sanitizeAmount(initialBalance) + income - expense);
  return { income, expense, balance };
}

// ─── MOTOR DE PARCELAMENTO (Artigo 80.1) ─────────────────────────────────────
// Gera parcelas com correção de centavos na última (anti-dízima) e clamp seguro
// de datas (ex: Jan/31 + 1 mês = Fev/28, não Mar/03).
export function generateInstallments(totalAmount, installmentsCount, firstDueDate, baseConfig = {}) {
  const count = Math.max(1, Math.floor(Number(installmentsCount) || 1));
  const total = sanitizeAmount(Number(totalAmount) || 0);
  const base  = sanitizeAmount(total / count);

  const [yr, mo, dy] = String(firstDueDate || new Date().toISOString().split('T')[0])
    .split('-').map(Number);

  const result = [];
  let allocated = 0;

  for (let i = 0; i < count; i++) {
    const isLast = i === count - 1;

    const rawMonth   = mo - 1 + i;
    const targetYear = yr + Math.floor(rawMonth / 12);
    const targetMon  = ((rawMonth % 12) + 12) % 12;
    const lastDay    = new Date(targetYear, targetMon + 1, 0).getDate();
    const safeDay    = Math.min(dy || 1, lastDay);
    const dueDateStr = `${String(targetYear).padStart(4, '0')}-${String(targetMon + 1).padStart(2, '0')}-${String(safeDay).padStart(2, '0')}`;

    const amount = isLast ? sanitizeAmount(total - allocated) : base;
    allocated    = sanitizeAmount(allocated + amount);

    result.push({
      type:           TRANSACTION_TYPES.INCOME,
      status:         TRANSACTION_STATUS.PENDING,
      amount,
      description:    `${String(baseConfig.description || 'Honorários').trim()} (${i + 1}/${count})`,
      category:       String(baseConfig.category || 'Honorários Advocatícios').trim(),
      competenceDate: dueDateStr,
      dueDate:        dueDateStr,
      date:           dueDateStr,
      paymentDate:    null,
      clientId:       baseConfig.clientId    || null,
      workspaceId:    baseConfig.workspaceId || null,
      contractId:     baseConfig.contractId  || null,
      group:          'RECEITA_BRUTA',
      movementType:   'CREDIT',
      module:         'contract',
      isPaid:         false,
      isDeleted:      false,
    });
  }

  return result;
}

// ─── SUMARIZAÇÃO MENSAL ───────────────────────────────────────────────────────
// Retorna mapa { "YYYY-MM": { income, expense, balance } }
export function summarizeByMonth(transactions = [], initialBalance = 0, regime = VIEW_REGIME.CASH) {
  const monthly = {};

  for (const tx of transactions.filter(isActiveTransaction)) {
    if (regime === VIEW_REGIME.CASH && tx.status !== TRANSACTION_STATUS.PAID) continue;

    const dateStr = getTransactionDate(tx, regime);
    if (!dateStr) continue;
    const month = String(dateStr).slice(0, 7); // "YYYY-MM"

    if (!monthly[month]) monthly[month] = { income: 0, expense: 0 };

    const amt = sanitizeAmount(tx.amount ?? tx.amountCents ?? 0);
    if (tx.type === TRANSACTION_TYPES.INCOME) {
      monthly[month].income = sanitizeAmount(monthly[month].income + amt);
    } else {
      monthly[month].expense = sanitizeAmount(monthly[month].expense + amt);
    }
  }

  // Calcular saldo de cada mês em ordem
  const sorted = Object.keys(monthly).sort();
  let running = sanitizeAmount(initialBalance);
  const result = {};
  for (const m of sorted) {
    running = sanitizeAmount(running + monthly[m].income - monthly[m].expense);
    result[m] = { ...monthly[m], balance: running };
  }

  return result;
}
